import { action } from '@ember/object';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { Currency } from 'lumen/models';

interface SettingsCurrencyConversionDropdownsBaseCurrencySignature {
  Args: {
    currencies: Currency[];
    onSelect: (currency: Currency) => void;
    selectedCurrency: Currency | null;
  };
}

type BaseCurrencyOption = {
  currency: Currency;
  id: string;
  label: string;
};

export default class SettingsCurrencyConversionDropdownBaseCurrency extends Component<SettingsCurrencyConversionDropdownsBaseCurrencySignature> {
  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currencyOptions() {
    return this.args.currencies.map((currency) => ({
      id: currency.code,
      label: `${currency.name} (${currency.symbol})`,
      currency,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOption() {
    if (!this.args.selectedCurrency) {
      return null;
    }
    return this.currencyOptions.find(
      (option) => option.id === this.args.selectedCurrency!.code,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  searchMatcher(option: BaseCurrencyOption, searchText: string) {
    const searchLower = searchText.toLowerCase();
    const matchesCurrency =
      option.label.toLowerCase().includes(searchLower) ||
      option.id.toLowerCase().includes(searchLower);
    return matchesCurrency ? 1 : -1;
  }

  @action handleSelect(option: BaseCurrencyOption): void {
    this.args.onSelect(option.currency);
  }

  <template>
    <div
      class="currency-select-input flex flex-col gap-100"
      data-test-id="currency-conversion-base-currency-dropdown"
    >
      <p class="text-200--bold">
        {{t "settings.currency_conversion.base_currency"}}
      </p>

      <PrismComboSelectInput
        @isSearchable={{true}}
        @onChange={{this.handleSelect}}
        @options={{this.currencyOptions}}
        @searchMatcher={{this.searchMatcher}}
        @unsafe_matchTriggerWidth={{true}}
        @value={{this.selectedOption}}
      />
    </div>
  </template>
}
